import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ReservationSummaryProps {
  reservationDetails: {
    email: string;
    startDate: Date;
    endDate: Date;
    purpose: string;
    notes: string;
  };
  vehicle: Vehicle;
  fleetName: string;
  onConfirm: () => void;
  onEditDates: () => void;
  onEditVehicle: () => void;
  loading?: boolean;
  error?: string | null;
}

const ReservationSummary: React.FC<ReservationSummaryProps> = ({
  reservationDetails,
  vehicle,
  fleetName,
  onConfirm,
  onEditDates,
  onEditVehicle,
  loading = false,
  error = null
}) => {
  const { t } = useTranslation();

  const formatDate = (date: Date) => {
    return format(date, 'EEEE dd MMMM yyyy à HH:mm', { locale: fr });
  };

  const calculateDuration = () => {
    const diffMs = reservationDetails.endDate.getTime() - reservationDetails.startDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0 && diffMinutes > 0) {
      return `${diffHours}h${diffMinutes}min`;
    } else if (diffHours > 0) {
      return `${diffHours}h`;
    } else {
      return `${diffMinutes}min`;
    }
  };

  return (
    <Paper sx={{ p: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          {t('reservation:summary.title', 'Récapitulatif de votre réservation')}
        </Typography>
        <Typography color="text.secondary">
          {t('reservation:summary.subtitle', 'Vérifiez les informations avant de confirmer')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Informations du véhicule */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary">
            {t('reservation:summary.vehicle', 'Véhicule')}
          </Typography>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={onEditVehicle}
            disabled={loading}
          >
            {t('common.modify', 'Modifier')}
          </Button>
        </Box>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {vehicle.make} {vehicle.model}
          </Typography>
          {vehicle.licensePlate && (
            <Typography variant="body2" color="text.secondary">
              {t('vehicle:licensePlate', 'Immatriculation')} : {vehicle.licensePlate}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('reservation:summary.site', 'Site')} : {fleetName}
          </Typography>
        </Paper>
      </Box>

      {/* Période de réservation */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="primary">
            {t('reservation:summary.period', 'Période')}
          </Typography>
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={onEditDates}
            disabled={loading}
          >
            {t('common.modify', 'Modifier')}
          </Button>
        </Box>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t('reservation:summary.from', 'Début')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(reservationDetails.startDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t('reservation:summary.to', 'Fin')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(reservationDetails.endDate)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Chip
                label={`${t('reservation:summary.duration', 'Durée')} : ${calculateDuration()}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Informations de réservation */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
          {t('reservation:summary.details', 'Détails')}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('reservation:summary.email', 'Email')}
              </Typography>
              <Typography variant="body1">{reservationDetails.email}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {t('reservation:summary.purpose', 'Motif de déplacement')}
              </Typography>
              <Typography variant="body1">{reservationDetails.purpose}</Typography>
            </Grid>
            {reservationDetails.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t('reservation:summary.notes', 'Notes')}
                </Typography>
                <Typography variant="body1">{reservationDetails.notes}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Bouton de confirmation */}
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          sx={{ minWidth: 200 }}
        >
          {loading
            ? t('reservation:summary.confirming', 'Confirmation...')
            : t('reservation:summary.confirm', 'Confirmer la réservation')}
        </Button>
      </Box>
    </Paper>
  );
};

export default ReservationSummary;
